import type { BookableSettings } from "@@/shared/acquire/bookable-settings";
import type { ResourceReservationRequest } from "@@/shared/acquire/resources/resource-reservation-request";
import { asDate } from "@@/shared/date-time/as-date";
import { SECOND } from "@@/shared/date-time/time-constants";
import { convertTimeByGranularity } from "@@/shared/date-time/time-granularity";
import type { TimeRange } from "@@/shared/date-time/time-range";
import { timeSlotByDurationFactory } from "@@/shared/date-time/time-slot";
import type { DurationInMinutes } from "@@/shared/date-time/time-spans";
import { addSeconds } from "date-fns";
import type {
    ResourceReservation} from "./resource-reservation";
import {
    isResourceReservation
} from "./resource-reservation";
import { resourceReservationRequestFactoryInfo } from "./resource-reservation-info";

const calculateSpreadTime = (params: {
    postTime: DurationInMinutes;
    preparationTime: DurationInMinutes;
    bookedTimeRange: TimeRange;
}) => {
    const { postTime, preparationTime, bookedTimeRange } = params;

    const preparationTimeInSeconds = convertTimeByGranularity(
        preparationTime,
        "MINUTE",
        "SECOND",
    );
    const totalDuration =
        convertTimeByGranularity(
            bookedTimeRange.duration,
            "SECOND",
            "MILLISECOND",
        ) +
        convertTimeByGranularity(
            postTime + preparationTime,
            "MINUTE",
            "MILLISECOND",
        );

    //-1 second to not include the exact start and end time.
    const timeRange = timeSlotByDurationFactory(
        addSeconds(asDate(bookedTimeRange.start), 1 - preparationTimeInSeconds),
        //Adjust for exact match
        totalDuration - 2 * SECOND,
    );
    return timeRange;
};

// Calculate spread time, spread time is the actual time range of the reservation
// with lead time and postTime included.
const spreadTimeFactory = (params: {
    settings?: BookableSettings;
    time: TimeRange;
}) => {
    const { settings, time } = params;
    const postTime: DurationInMinutes =
        settings?.postTime ?? (0 as DurationInMinutes);

    const preparationTime: DurationInMinutes =
        settings?.preparationTime ?? (0 as DurationInMinutes);

    const spreadTime = calculateSpreadTime({
        postTime,
        preparationTime,
        bookedTimeRange: time,
    });
    return spreadTime;
};

const toResourceReservationRequests = (
    reservation: ResourceReservation | ResourceReservationRequest,
) => {
    if (isResourceReservation(reservation) && reservation.spreadTime)
        return resourceReservationRequestFactoryInfo(
            reservation,
            reservation.spreadTime,
        );

    const spreadTime = spreadTimeFactory({
        time: reservation.timeRange,
        settings: isResourceReservation(reservation)
            ? reservation.bookableSettings
            : undefined,
    });
    return resourceReservationRequestFactoryInfo(reservation, spreadTime);
};

export {
    calculateSpreadTime,
    spreadTimeFactory,
    toResourceReservationRequests,
};
